"use client";

import { useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { useEmailAgentConfig } from "@/hooks/agents/email";
import { AgentKnowledgeBaseSettings } from "@/components/agents/shared/knowledge-base-settings";
import type { AgentRagSettings } from "@/lib/agents/shared/types";

interface KnowledgeBaseSettingsProps {
  agentId: string;
}

export function KnowledgeBaseSettings({ agentId }: KnowledgeBaseSettingsProps) {
  const { config, isLoading, loadConfig, saveConfig } = useEmailAgentConfig(agentId);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveKnowledgeBases = useCallback(
    async (kbIds: string[]): Promise<boolean> => {
      try {
        await saveConfig({ knowledge_bases: kbIds });
        return true;
      } catch {
        return false;
      }
    },
    [saveConfig]
  );

  const saveRagSettings = useCallback(
    async (settings: AgentRagSettings): Promise<boolean> => {
      try {
        await saveConfig({ rag_settings: settings });
        return true;
      } catch {
        return false;
      }
    },
    [saveConfig]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <AgentKnowledgeBaseSettings
      agentId={agentId}
      selectedKnowledgeBases={config.knowledge_bases || []}
      ragSettings={config.rag_settings}
      onSaveKnowledgeBases={saveKnowledgeBases}
      onSaveAgentRagSettings={saveRagSettings}
    />
  );
}
